import { cn } from "@/lib/utils";

interface Win2kWindowProps {
  title: string;
  children: React.ReactNode;
  statusBar?: React.ReactNode;
  menuBar?: React.ReactNode;
  className?: string;
  onClose?: () => void;
}

function TitleBarButton({ label, onClick }: { label: string; onClick?: () => void }) {
  const enabled = !!onClick;
  return (
    <button
      type="button"
      className={cn(
        "win2k-outset w-4 h-3.5 flex items-center justify-center bg-[var(--win2k-button-face)] text-[8px] leading-none font-bold",
        enabled
          ? "cursor-pointer text-black hover:brightness-95 active:win2k-inset"
          : "cursor-default opacity-50"
      )}
      tabIndex={-1}
      onClick={onClick}
      disabled={!enabled}
    >
      {label}
    </button>
  );
}

export default function Win2kWindow({
  title,
  children,
  statusBar,
  menuBar,
  className,
  onClose,
}: Win2kWindowProps) {
  return (
    <div
      className={cn(
        "win2k-outset bg-[var(--win2k-window-bg)] flex flex-col",
        "w-full max-w-lg mx-auto",
        className
      )}
    >
      {/* Title Bar */}
      <div className="win2k-title-bar gap-2 px-1 py-0.5 min-h-[22px] shrink-0">
        <span className="flex-1 truncate text-xs font-bold">
          {title}
        </span>
        <div className="flex gap-px">
          <TitleBarButton label="—" />
          <TitleBarButton label="□" />
          <TitleBarButton label="×" onClick={onClose} />
        </div>
      </div>

      {/* Menu Bar */}
      {menuBar && (
        <div className="flex bg-[var(--win2k-gray)] px-1 py-0.5 gap-3 text-xs border-b border-[var(--win2k-shadow)]">
          {menuBar}
        </div>
      )}

      {/* Content Area */}
      <div className="flex-1 min-h-0 p-2">{children}</div>

      {/* Status Bar */}
      {statusBar !== undefined && (
        <div className="win2k-inset mx-1 mb-1 px-2 py-0.5 text-[11px] text-[var(--win2k-button-text)] bg-[var(--win2k-gray)]">
          {statusBar}
        </div>
      )}
    </div>
  );
}
